import {pathToRegexp} from 'path-to-regexp';
import qs, {ParsedQs} from 'qs';
import {getEnv} from '../common/env/env';
import {APP_LINK_FALLBACK_QUERY_KEY} from '../domain/bridge/applink/constant';
import {AppLinkFallbackType} from './AppLinkFallbackType.enum';
import {PagewebPage} from './PagewebPage.enum';
import {pagewebPageUrlPatternMap} from './PagewebPageUrlPatternMap';
import {PagewebUrlPattern} from './PagewebUrlPattern.interface';

/**
 * PagewebUrl 을 관리합니다
 */
export class PagewebUrlMatcher {
  _defaultPattern?: PagewebUrlPattern;
  _patterns: PagewebUrlPattern[] = [];

  constructor() {
    // 패턴을 추가한 순서대로 매칭됩니다
    // 매치된 path 는 바로 리턴되는 부분 참고해주세요
    // 좀더 빠른 매칭을 위해 많이 노출되는 페이지를 상단에 추가해주세요

    this.addPattern({
      isDefault: true, // 앱다운로드 버튼이나 모르는 케이스엔 main 을 디폴트로 한다
      page: PagewebPage.OpenMain,
    });

    [
      PagewebPage.Main,
      PagewebPage.Viewer,
      PagewebPage.Content,
      PagewebPage.SetOverview,
      PagewebPage.Subtab,
      PagewebPage.InvenRecent,
      PagewebPage.InvenLike,
      PagewebPage.InvenPaid,
      PagewebPage.NoticeWithId,
      PagewebPage.Notice,
      PagewebPage.AppStore,
      PagewebPage.CashHistory,
      PagewebPage.TicketHistory,
      PagewebPage.LandingSeriesListWithTheme,
      PagewebPage.LandingSingleListWithTheme,
      PagewebPage.LandingSeriesPosterWithTheme,
      PagewebPage.LandingSeriesList,
      PagewebPage.LandingSingleList,
      PagewebPage.LandingSeriesPoster,
      PagewebPage.LandingSetList,
      PagewebPage.LandingSetPoster,
      PagewebPage.LandingTodayUpSubcategory,
      PagewebPage.LandingTodayUp,
      PagewebPage.LandingTodayNewSubcategory,
      PagewebPage.LandingTodayNew,
      PagewebPage.LandingOriginalSubcategory,
      PagewebPage.LandingOriginal,
      PagewebPage.LandingEventTotal,
      PagewebPage.LandingEvent,
      PagewebPage.LandingRankingSubcategory,
      PagewebPage.LandingRanking,
      PagewebPage.LandingGenreSubcategory,
      PagewebPage.LandingGenre,
      PagewebPage.DayofweekSubcategory,
      PagewebPage.Dayofweek,
      PagewebPage.Freeseries,
      PagewebPage.WebviewEvent,
      PagewebPage.Gacha,
      PagewebPage.Redeem,
      PagewebPage.TodayGiftListWithCategory,
      PagewebPage.TodayGiftList,
      PagewebPage.CommentsDetail,
      PagewebPage.Comments,
      PagewebPage.BuyCash,
      PagewebPage.BuyTicket,
      PagewebPage.Cashfriends,
      /** @deprecated */
      PagewebPage.KeytalkSearch,
      PagewebPage.CashSponsor,
      PagewebPage.Autocharge,
      /** @deprecated */
      PagewebPage.SearchKeytalk,
      PagewebPage.SearchFilter,
      PagewebPage.SearchSeries,
      PagewebPage.SearchMain,
      PagewebPage.SpecialGift,
      PagewebPage.MyNews,
      PagewebPage.MyNewsWithTab,
    ].forEach(page => {
      this.addPattern({page});
    });
  }

  /**
   * 패턴 추가 순서대로 비교하기 때문에 반드시 순서대로 넣어야합니다
   */
  private addPattern({
    isDefault,
    page,
  }: {
    /**
     * 기본 여부. 메인이 들어갑니다
     */
    isDefault?: boolean;
    page: PagewebPage;
  }) {
    const {pathQueryPattern, schemePattern} = pagewebPageUrlPatternMap[page];
    const pathLastIndex = pathQueryPattern.indexOf('?');
    const pathPattern =
      pathLastIndex > -1
        ? pathQueryPattern.substring(0, pathLastIndex)
        : pathQueryPattern;
    const queryPattern =
      pathLastIndex > -1
        ? pathQueryPattern.substring(pathLastIndex + 1, pathQueryPattern.length)
        : undefined;
    const variableRegExp = /(\{[^}]+\})/g;
    const orderedPathVariables = pathPattern.match(variableRegExp) || [];
    const pathRegExp = pathToRegexp(
      pathPattern.replace(variableRegExp, ':foo')
    );

    const schemePathLastIndex = schemePattern.indexOf('?');
    const schemePath =
      schemePathLastIndex > -1
        ? schemePattern.substring(0, schemePathLastIndex)
        : schemePattern;
    const schemeQueryPattern =
      schemePathLastIndex > -1
        ? schemePattern.substring(schemePathLastIndex + 1, schemePattern.length)
        : undefined;

    this._patterns.push({
      page,
      pathQueryPattern: {
        raw: pathQueryPattern,
        pathRegExp,
        orderedPathVariables,
        query: queryPattern !== undefined ? qs.parse(queryPattern) : undefined,
      },
      schemePattern: {
        raw: schemePattern,
        path: schemePath,
        query:
          schemeQueryPattern !== undefined
            ? qs.parse(schemeQueryPattern)
            : undefined,
      },
    });
    if (isDefault) {
      this._defaultPattern = this._patterns[this._patterns.length - 1];
    }
  }

  get patterns() {
    return this._patterns;
  }

  getPageByPathQuery(pathQuery: string): PagewebPage {
    for (let i = 0; i < this._patterns.length; i++) {
      const pattern = this._patterns[i];
      const matched = pattern.pathQueryPattern.pathRegExp.exec(pathQuery);
      if (!matched) {
        continue;
      }
      return pattern.page;
    }
    return PagewebPage.Main;
  }

  getPageByUrl(url: URL): PagewebPage {
    return this.getPageByPathQuery(url.pathname + url.search);
  }

  getCurrentPage(): PagewebPage {
    return this.getPageByPathQuery(
      window.location.pathname + window.location.search
    );
  }

  getAppLinkByPathQuery({
    pathQuery,
    schemePrefixType,
    fallbackType,
  }: {
    pathQuery: string;
    schemePrefixType: 'applink' | 'scheme';
    fallbackType?: AppLinkFallbackType;
  }) {
    const url = new URL(pathQuery, 'http://example.com'); // base 는 왜 꼭 넣어야되는건지..
    return this.getAppLinkByUrl({url, schemePrefixType, fallbackType});
  }

  getAppLinkByUrl({
    url,
    schemePrefixType,
    fallbackType,
  }: {
    url: URL;
    schemePrefixType: 'applink' | 'scheme';
    fallbackType?: AppLinkFallbackType;
  }): string {
    for (let i = 0; i < this._patterns.length; i++) {
      const pattern = this._patterns[i];
      const matched = pattern.pathQueryPattern.pathRegExp.exec(url.pathname);
      if (!matched) {
        continue;
      }
      const resultSchemeQuery = {...pattern.schemePattern.query};
      // /content/{series_id}/viewer/{product_id} 에서 {series_id}, {product_id} 를 변환
      pattern.pathQueryPattern.orderedPathVariables.forEach(
        (pathVariable, index) => {
          // scheme 의 variable 을 매치된 값으로 치환
          Object.keys(resultSchemeQuery).forEach(paramKey => {
            if (resultSchemeQuery[paramKey] === pathVariable) {
              resultSchemeQuery[paramKey] = matched[index + 1];
              url.searchParams.delete(paramKey);
            }
          });
        }
      );
      if (pattern.pathQueryPattern.query) {
        Object.keys(pattern.pathQueryPattern.query).forEach(existQueryKey => {
          const variable = pattern.pathQueryPattern?.query?.[existQueryKey];
          const value = url.searchParams.get(existQueryKey);
          Object.keys(resultSchemeQuery).forEach(paramKey => {
            // viewer_id={product_id} 일 때 {product_id} 가 매치된다면
            if (resultSchemeQuery[paramKey] === variable) {
              resultSchemeQuery[paramKey] = value || '';
              url.searchParams.delete(existQueryKey); // 매치되는게 있다면 추가한 쿼리파람이니 일괄 추가 시 skip 한다
            }
          });
        });
      }
      this.putSearchParamsAndFallback(
        resultSchemeQuery,
        url.searchParams,
        fallbackType
      );
      return this.generateReturnUrl(
        schemePrefixType,
        pattern.schemePattern.path,
        resultSchemeQuery
      );
    }
    // 매치되는게 없다면 메인으로 보낸다
    const resultQuery: ParsedQs = {};
    this.putSearchParamsAndFallback(
      resultQuery,
      url.searchParams,
      fallbackType
    );
    return this.generateReturnUrl(
      schemePrefixType,
      this._defaultPattern?.schemePattern.path || '/main',
      resultQuery
    );
  }

  // 웹에서는 지원하지 않는 스킴에 대해서도 앱링크를 생성합니다.
  getAppLinkByScheme({
    scheme,
    fallbackType,
  }: {
    scheme: string;
    fallbackType?: AppLinkFallbackType;
  }) {
    const schemeAction = scheme.split('kakaopage:/')[1];
    const query = new URL(
      schemeAction,
      'http://example.com'
    ).searchParams.toString();
    return `${this.getSchemePrefix('applink')}${schemeAction}${
      fallbackType
        ? query
          ? `&alfallback=${fallbackType}`
          : `?alfallback=${fallbackType}`
        : ''
    }`;
  }

  private generateReturnUrl(
    schemePrefixType: 'applink' | 'scheme',
    path: string,
    query?: ParsedQs
  ) {
    return `${this.getSchemePrefix(schemePrefixType)}${path}${
      query && Object.keys(query).length > 0 ? `?${qs.stringify(query)}` : ''
    }`;
  }

  getCurrentAppLink({
    schemePrefixType,
    fallbackType,
  }: {
    schemePrefixType: 'applink' | 'scheme';
    fallbackType?: AppLinkFallbackType;
  }) {
    return this.getAppLinkByUrl({
      url: new URL(window.location.href),
      schemePrefixType,
      fallbackType,
    });
  }

  getDefaultAppLink({
    schemePrefixType,
    fallbackType,
  }: {
    schemePrefixType: 'applink' | 'scheme';
    fallbackType?: AppLinkFallbackType;
  }): string {
    const resultQuery: ParsedQs = {};
    this.putSearchParamsAndFallback(
      resultQuery,
      new URL(window.location.href).searchParams,
      fallbackType
    );
    return `${this.getSchemePrefix(schemePrefixType)}${
      this._defaultPattern?.schemePattern.path
    }?${qs.stringify(resultQuery)}`;
  }

  private getSchemePrefix(schemePrefixType: 'applink' | 'scheme') {
    if (schemePrefixType === 'applink') {
      return `${getEnv()?.linkOrigin || 'https://link-page.kakao.com'}`;
    } else {
      return 'kakaopage:/';
    }
  }

  /**
   * mutable
   * query 에 인자로 받은 searchParam 과 fallback 시 처리를 추가함
   */
  private putSearchParamsAndFallback(
    query: ParsedQs,
    searchParams: URLSearchParams,
    fallbackType?: AppLinkFallbackType
  ) {
    searchParams.forEach((value, key) => {
      query[key] = value;
    });
    if (fallbackType) {
      query[APP_LINK_FALLBACK_QUERY_KEY] = fallbackType;
    }
  }
}
